import React, { useEffect, useState } from 'react'
import { gql, useQuery } from '@apollo/client'
import { useLocation } from 'react-router-dom'
import { Alert, Box, Button, Fade, Skeleton, Snackbar, Typography } from '@mui/material'
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined'
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import HourglassTopOutlinedIcon from '@mui/icons-material/HourglassTopOutlined'
import {
  ConnectComponentsProvider,
  ConnectAccountManagement,
  ConnectAccountOnboarding,
  ConnectNotificationBanner,
  ConnectBalances,
  ConnectDocuments,
  ConnectPayouts,
} from '@stripe/react-connect-js'
import { getStripeRestaurantDashboard, getStripeRestaurantOverview } from '../apollo/queries'
import {
  Card,
  CardHeader,
  OverviewStrip,
  RemoveAccountCard,
  SectionNav,
  SetupChecklist,
  StatusPill,
  TransactionsSection,
  color,
  focusRing,
  font,
  radius,
  type,
  useStripeConnect,
} from '../components/StripePayments'

const GET_DASHBOARD = gql`${getStripeRestaurantDashboard}`
const GET_OVERVIEW = gql`${getStripeRestaurantOverview}`

const SECTIONS = [
  { key: 'payouts', label: 'Payouts', Icon: AccountBalanceOutlinedIcon },
  { key: 'transactions', label: 'Transactions', Icon: ReceiptLongOutlinedIcon },
  { key: 'refunds', label: 'Refunds', Icon: UndoOutlinedIcon },
  { key: 'balance', label: 'Balance', Icon: AccountBalanceWalletOutlinedIcon },
  { key: 'documents', label: 'Tax documents', Icon: DescriptionOutlinedIcon },
  { key: 'settings', label: 'Settings', Icon: SettingsOutlinedIcon },
]

/** A Stripe embedded component in one of our cards, with our own loading state while Connect.js starts. */
function Embedded({ connect, error, title, subtitle, children }) {
  return (
    <Card>
      <CardHeader title={title} subtitle={subtitle} />
      {error && <Alert severity="error">{error}</Alert>}
      {!error && !connect && (
        <Box aria-busy="true">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} height={44} sx={{ bgcolor: '#EEF1F5', transform: 'none', mb: 1, borderRadius: radius.control }} />
          ))}
        </Box>
      )}
      {connect && <ConnectComponentsProvider connectInstance={connect}>{children}</ConnectComponentsProvider>}
    </Card>
  )
}

/**
 * Shown on an onboarded account while Stripe still has work to do (verifying,
 * or asking for something new). "Resume setup" only when the owner has
 * something left to fill in; it opens Stripe's form on this page.
 */
function VerificationNotice({ steps, onResume }) {
  const needsOwner = (steps || []).some((s) => s.status === 'TODO')

  return (
    <Box
      role="status"
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
        px: 2.5, py: 1.75, borderRadius: radius.card,
        bgcolor: color.warningSoft, border: '1px solid #F8DDB0',
      }}
    >
      <HourglassTopOutlinedIcon sx={{ color: color.warning, fontSize: 20 }} />
      <Typography sx={{ fontSize: 14, color: color.warningText, flex: '1 1 260px' }}>
        {needsOwner
          ? 'Stripe needs a few more details before payouts to your bank can start.'
          : 'Stripe is verifying your details. Payouts to your bank start as soon as it is done.'}
      </Typography>
      {needsOwner && onResume && (
        <Button
          onClick={onResume}
          sx={{ textTransform: 'none', fontWeight: 600, color: color.warningText, borderRadius: radius.control, ...focusRing }}
        >
          Resume setup
        </Button>
      )}
    </Box>
  )
}

export default function RestaurantStripe() {
  const restaurantId = localStorage.getItem('restaurantId')
  const location = useLocation()
  const setupParam = new URLSearchParams(location.search).get('setup')

  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery(GET_DASHBOARD, {
    variables: { restaurantId, refresh: true },
    skip: !restaurantId,
    fetchPolicy: 'network-only',
  })
  const dashboard = data?.stripeRestaurantDashboard
  const enabled = !!dashboard?.enabled
  const onboarded = !!dashboard?.detailsSubmitted
  const setupDone = onboarded && !!dashboard?.transfersActive && !!dashboard?.payoutsEnabled

  // Stripe's onboarding form, on this page. Starting Connect.js before the
  // owner asks for it would create a Stripe account for every restaurant that
  // merely opens the page, so it waits for the button.
  const [formOpen, setFormOpen] = useState(false)
  const [formWanted, setFormWanted] = useState(false)
  const [backFromForm, setBackFromForm] = useState(false)
  const openForm = () => {
    setFormWanted(true)
    setFormOpen(true)
  }
  const onFormExit = () => {
    setFormOpen(false)
    setBackFromForm(true)
    refetch()
  }

  // Connect.js only matters once there is a balance to show, or the form is open.
  const { connect, error: connectError } = useStripeConnect(restaurantId, enabled && (onboarded || formWanted))
  const { data: overviewData, loading: overviewLoading, error: overviewError } = useQuery(GET_OVERVIEW, {
    variables: { restaurantId },
    skip: !enabled || !onboarded,
    fetchPolicy: 'cache-and-network',
  })

  const [section, setSection] = useState('payouts')
  const [toast, setToast] = useState(null)
  const [reloadKey] = useState(0)

  // Back from Stripe's onboarding (its site or the form here): Stripe can take
  // a few seconds to mark the details submitted, so check every 10s for two
  // minutes. Afterwards, while Stripe is still verifying, once a minute.
  useEffect(() => {
    if (!enabled) return undefined
    if (!onboarded && (setupParam === 'return' || backFromForm)) {
      startPolling(10000)
      const stop = setTimeout(() => stopPolling(), 120000)
      return () => { clearTimeout(stop); stopPolling() }
    }
    if (onboarded && !setupDone) {
      startPolling(60000)
      return () => stopPolling()
    }
    return undefined
  }, [enabled, onboarded, setupDone, setupParam, backFromForm])

  return (
    <Box sx={{ bgcolor: color.page, minHeight: '100vh', fontFamily: font, px: { xs: 2, sm: 3, md: 5 }, py: { xs: 3, md: 5 } }}>
      <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
        {!restaurantId && <Alert severity="warning">Choose a restaurant first.</Alert>}
        {error && <Alert severity="error">{error.message}</Alert>}

        {loading && !dashboard && (
          <Box aria-busy="true" sx={{ maxWidth: 680 }}>
            <Skeleton width={220} height={44} sx={{ bgcolor: '#E9EDF2' }} />
            <Skeleton width="70%" sx={{ bgcolor: '#E9EDF2', mb: 3 }} />
            <Skeleton variant="rectangular" height={320} sx={{ bgcolor: '#E9EDF2', borderRadius: radius.card }} />
          </Box>
        )}

        {dashboard && !enabled && (
          <Card sx={{ maxWidth: 680 }}>
            <Typography sx={type.sectionTitle}>Payments are not turned on</Typography>
            <Typography sx={{ ...type.small, mt: 0.75 }}>
              RushmyFood has not turned on Stripe payouts for this restaurant yet.
            </Typography>
          </Card>
        )}

        {/* State A — not onboarded yet */}
        {enabled && !onboarded && (
          <SetupChecklist
            restaurantId={restaurantId}
            steps={dashboard.setupChecklist}
            pendingAmount={dashboard.pendingAmount}
            pendingOrders={dashboard.pendingOrders}
            processingFeePercent={dashboard.processingFeePercent}
            processingFeeFixed={dashboard.processingFeeFixed}
            autoStart={setupParam === 'refresh'}
            connect={connect}
            connectError={connectError}
            formOpen={formOpen}
            onOpenForm={openForm}
            onFormExit={onFormExit}
          />
        )}

        {/* State B — onboarded */}
        {enabled && onboarded && (
          // minmax(0, 1fr): without it the column grows to the transactions
          // table's minimum width and the whole page scrolls sideways on a phone,
          // instead of the table scrolling inside its card.
          <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: { xs: 3, md: 4 } }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography component="h1" sx={type.pageTitle}>Payments</Typography>
                <Typography sx={{ ...type.body, color: color.muted, mt: 0.75 }}>
                  Powered by Stripe.
                  {dashboard.processingFeePercent != null && (
                    ` A card processing fee of ${Number(dashboard.processingFeePercent)}% + $${Number(dashboard.processingFeeFixed || 0).toFixed(2)} comes out of your share on each card order.`
                  )}
                </Typography>
              </Box>
              {overviewData?.stripeRestaurantOverview?.payoutSchedule && (
                <StatusPill tone="accent">Payouts: {overviewData.stripeRestaurantOverview.payoutSchedule}</StatusPill>
              )}
            </Box>

            {!setupDone && <VerificationNotice steps={dashboard.setupChecklist} onResume={formOpen ? null : openForm} />}

            {formOpen && (
              <Embedded
                connect={connect}
                error={connectError}
                title="Finish setup"
                subtitle="Add what Stripe still needs. Payouts to your bank start once it is all in."
              >
                <ConnectAccountOnboarding collectionOptions={{ fields: 'eventually_due' }} onExit={onFormExit} />
              </Embedded>
            )}

            {connect && (
              <ConnectComponentsProvider connectInstance={connect}>
                <ConnectNotificationBanner />
              </ConnectComponentsProvider>
            )}

            {overviewError
              ? <Alert severity="error">{overviewError.message}</Alert>
              : <OverviewStrip overview={overviewData?.stripeRestaurantOverview} loading={overviewLoading} />}

            <Box>
              <SectionNav sections={SECTIONS} value={section} onChange={setSection} />
              <Fade in key={section} timeout={180}>
                <Box role="tabpanel" id={`payments-panel-${section}`} aria-labelledby={`payments-tab-${section}`} sx={{ mt: 2.5 }}>
                  {section === 'payouts' && (
                    <Embedded
                      connect={connect}
                      error={connectError}
                      title="Payouts"
                      subtitle="Pay out your balance, change your payout schedule and see every payout to your bank."
                    >
                      <ConnectPayouts />
                    </Embedded>
                  )}
                  {section === 'transactions' && (
                    <TransactionsSection restaurantId={restaurantId} reloadKey={reloadKey} />
                  )}
                  {section === 'refunds' && (
                    <TransactionsSection restaurantId={restaurantId} refundedOnly reloadKey={reloadKey} />
                  )}
                  {section === 'balance' && (
                    <Embedded connect={connect} error={connectError} title="Balance" subtitle="What is ready to pay out, and what is still on its way.">
                      <ConnectBalances />
                    </Embedded>
                  )}
                  {section === 'documents' && (
                    <Embedded connect={connect} error={connectError} title="Tax documents" subtitle="Tax forms Stripe issues for your account, such as 1099s.">
                      <ConnectDocuments />
                    </Embedded>
                  )}
                  {section === 'settings' && (
                    <Box sx={{ display: 'grid', gap: { xs: 2.5, md: 3 } }}>
                      <Embedded
                        connect={connect}
                        error={connectError}
                        title="Business and bank details"
                        subtitle="The details Stripe has on file, including the bank account your payouts go to."
                      >
                        <ConnectAccountManagement />
                      </Embedded>
                      <RemoveAccountCard
                        restaurantId={restaurantId}
                        onRemoved={() => {
                          // Back to the start: the page drops to "Set up payouts".
                          setSection('payouts')
                          refetch()
                          setToast({ severity: 'success', text: 'Your Stripe account was removed. You can set up payouts again at any time.' })
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </Fade>
            </Box>
          </Box>
        )}
      </Box>

      <Snackbar
        open={!!toast}
        autoHideDuration={8000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.text}</Alert> : null}
      </Snackbar>
    </Box>
  )
}
